import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import api from "../../service/api";
import { useStore } from "../../store";
import { IEventApcap } from "../../@types/system";
import { Button } from "../Button";
import { Textarea } from "../Form/Textarea";
import { Loading } from "../Loading";
import Certificado from "../Certificado";
import { CertificateNamePositionPanel } from "../certificate-name-position-panel";
import {
  DEFAULT_CERTIFICATE_NAME_LAYOUT,
  type CertificateNameLayout,
} from "../../types/certificate-name-layout.interface";

interface ISelectedCert {
  id: string;
  name: string;
}

/** Base do arquivo em `public/`: `{id}.jpg` → certificado_sem_evento.jpg */
const CERTIFICADO_SEM_EVENTO_ID = "certificado_sem_evento";

export function CertificadoLoteAdmin() {
  const events = useStore((s) => s.events);
  const setEvents = useStore((s) => s.setEvents);

  const [loadingEvents, setLoadingEvents] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [namesRaw, setNamesRaw] = useState("");
  const [selectedItems, setSelectedItems] = useState<ISelectedCert[]>([]);
  const [showCertificates, setShowCertificates] = useState(false);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [certificateNameLayout, setCertificateNameLayout] =
    useState<CertificateNameLayout>(() => ({
      ...DEFAULT_CERTIFICATE_NAME_LAYOUT,
    }));

  const selectedEvent = useMemo(
    () => events.find((e) => e.id === selectedEventId),
    [events, selectedEventId]
  );

  const selectedCertificateTitle = useMemo(() => {
    if (selectedEventId === CERTIFICADO_SEM_EVENTO_ID) {
      return "Certificado genérico (sem evento)";
    }
    return selectedEvent?.name ?? "";
  }, [selectedEventId, selectedEvent?.name]);

  useEffect(() => {
    async function load() {
      setLoadingEvents(true);
      try {
        const res = await api.get<IEventApcap[]>(`/events`);
        setEvents(res.data);
      } catch {
        toast.error("Erro ao carregar eventos.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      } finally {
        setLoadingEvents(false);
      }
    }
    load();
  }, [setEvents]);

  useEffect(() => {
    setShowCertificates(false);
    setSelectedItems([]);
  }, [selectedEventId]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowStickyHeader(true);
      } else {
        setShowStickyHeader(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  function parseNames(raw: string): string[] {
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }

  function handleGenerateCertificates() {
    if (!selectedEventId) {
      toast.error("Selecione o modelo do certificado.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }

    const names = parseNames(namesRaw);
    if (!names.length) {
      toast.error("Informe pelo menos um nome, separados por vírgula.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }

    const items: ISelectedCert[] = names.map((name, index) => ({
      id: `lote-${index}`,
      name,
    }));
    setSelectedItems(items);
    setShowCertificates(true);
  }

  async function handlePDF() {
    if (!selectedItems.length) {
      toast.error("Não há nomes para gerar o PDF.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }

    setLoadingPDF(true);

    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "px",
      format: "a4",
    });

    const generatePageFromDiv = async (id: string, isFirstPage: boolean) => {
      const element = window.document.getElementById(id);

      if (element) {
        const canvas = await html2canvas(element);
        const imgData = canvas.toDataURL("image/png");
        const imgWidth = pdf.internal.pageSize.getWidth();
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (!isFirstPage) {
          pdf.addPage();
        }

        pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      } else {
        console.error(`Elemento com o ID '${id}' não encontrado.`);
      }
    };

    try {
      for (let i = 0; i < selectedItems.length; i++) {
        const id_to_print = `print_${selectedItems[i].id}`;
        await generatePageFromDiv(id_to_print, i === 0);
      }
      pdf.save("certificados_capacita_brasil.pdf");
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      toast.error("Ocorreu um erro ao gerar o PDF.");
    } finally {
      setLoadingPDF(false);
    }
  }

  return (
    <>
      {loadingPDF && (
        <div className="fixed top-0 left-0 z-50 flex h-full w-full items-center justify-center bg-black bg-opacity-50">
          <Loading />
        </div>
      )}

      {loadingEvents && !showCertificates && <Loading />}

      {!loadingEvents && !showCertificates && (
        <div className="mt-6 space-y-6 rounded-lg border border-zinc-200 bg-white p-6 dark:border-zinc-700 dark:bg-zinc-900">
          <div>
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
              Certificados em lote
            </h2>
            <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
              Escolha o modelo: eventos usam a imagem{" "}
              <code className="rounded bg-zinc-100 px-1 dark:bg-zinc-800">
                {"{id do evento}.jpg"}
              </code>{" "}
              na pasta pública (como no credenciamento), ou o modelo fixo{" "}
              <code className="rounded bg-zinc-100 px-1 dark:bg-zinc-800">
                certificado_sem_evento.jpg
              </code>
              . Depois informe os nomes separados por vírgula.
            </p>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="cert-lote-evento"
              className="block text-sm font-medium text-zinc-700 dark:text-zinc-300"
            >
              Modelo do certificado
            </label>
            <select
              id="cert-lote-evento"
              className="w-full max-w-xl rounded-lg border border-zinc-300 bg-white px-3 py-2 text-zinc-900 shadow-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/30 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
            >
              <option value="">Selecione um modelo</option>
              <option value={CERTIFICADO_SEM_EVENTO_ID}>
                Certificado genérico (certificado_sem_evento.jpg)
              </option>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.name}
                </option>
              ))}
            </select>
            {selectedEventId ? (
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                Arquivo esperado:{" "}
                <code className="rounded bg-zinc-100 px-1 dark:bg-zinc-800">
                  {selectedEventId}.jpg
                </code>
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <label
              htmlFor="cert-lote-nomes"
              className="block text-sm font-medium text-zinc-700 dark:text-zinc-300"
            >
              Nomes (separados por vírgula)
            </label>
            <Textarea
              id="cert-lote-nomes"
              placeholder="Ex.: Maria Silva, João Santos, Ana Paula"
              value={namesRaw}
              onChange={(e) => setNamesRaw(e.target.value)}
              className="min-h-[160px] font-mono text-sm"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              className="bg-green-700 text-white hover:bg-green-800"
              onClick={handleGenerateCertificates}
            >
              Gerar certificados
            </Button>
          </div>
        </div>
      )}

      {!loadingEvents && showCertificates && selectedItems.length > 0 && (
        <div className="mt-6 flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
            <div>
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
                {selectedCertificateTitle || "Certificados"}
              </h2>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                {selectedItems.length} certificado(s) — mesmo processo de
                visualização e PDF do credenciamento.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                className="bg-violet-700 text-white hover:bg-violet-800"
                onClick={handlePDF}
              >
                Baixar certificados
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCertificates(false)}
              >
                Voltar
              </Button>
            </div>
          </div>

          <CertificateNamePositionPanel
            value={certificateNameLayout}
            onChange={setCertificateNameLayout}
          />

          {selectedItems.map((item) => (
            <div key={item.id} className="border shadow-sm">
              <Certificado
                name={item.name}
                id={item.id}
                event_id={selectedEventId}
                nameLayout={certificateNameLayout}
              />
            </div>
          ))}
        </div>
      )}

      {!loadingEvents && showCertificates && showStickyHeader && (
        <div className="sticky bottom-0 z-20 mt-4 border bg-violet-500 p-4 shadow-xl dark:bg-zinc-800">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-xl font-bold text-white">
              {selectedCertificateTitle}
            </h2>
            <div className="flex flex-wrap gap-2">
              <Button
                className="bg-violet-700 text-white hover:bg-violet-800"
                onClick={handlePDF}
              >
                Baixar certificados
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowCertificates(false)}
                className="bg-white hover:bg-gray-200"
              >
                Voltar
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
